import { PROPERTY_GRID_SUBJECT_PROPERTY } from '@aloft/toolkit-shared';

import { FieldPath, useWatch } from 'react-hook-form';

import { selectHasArvOption } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridRow } from '../rows/PropertyGridRow.tsx';
import { TitleRow } from '../rows/TitleRow.tsx';
import {
  PropertyGridSectionProps as BasePropertyGridSectionProps,
  PropertyGridField,
  PropertyGridForm,
} from '../types.ts';

type PropertyGridSectionProps = BasePropertyGridSectionProps & {
  title: string;
  fields: PropertyGridField[];
};

export const PropertyGridSection = ({
  fields,
  title,
  isRemovable,
  onRemove,
  ...propertySectionProps
}: PropertyGridSectionProps) => {
  const hasArvOption = useAppSelector(selectHasArvOption);
  const { columnIndex, fieldTypes, feature, control, getValues } = propertySectionProps;

  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });
  const isOtherFeature = feature === 'other';
  const hasFeatureKey = feature === 'manualCost' ? 'hasManualCost' : `${feature}.hasFeature`;
  const featurePath = feature
    ? `columns.${columnIndex}.propertyData.editableData.improvements.${hasFeatureKey}`
    : `columns.${columnIndex}.propertyId`;
  const arvFeaturePath = feature
    ? `columns.${columnIndex}.propertyData.editableData.arv.improvements.${hasFeatureKey}`
    : `columns.${columnIndex}.propertyId`;
  const otherDisplayNamePath =
    `columns.${columnIndex}.propertyData.editableData.improvements.otherAdjustmentsDisplayName` as const;

  const watchedValue: boolean = useWatch({
    control,
    name: featurePath as FieldPath<PropertyGridForm>,
  }) as boolean;
  const arvWatchedValue: boolean = useWatch({
    control,
    name: arvFeaturePath as FieldPath<PropertyGridForm>,
  }) as boolean;
  const otherDisplayName = useWatch({
    control,
    name: otherDisplayNamePath as FieldPath<PropertyGridForm>,
  }) as string | undefined;

  let sectionIsNone = false;
  let arvSectionIsNone = false;

  if (isOtherFeature) {
    if (propertyId !== PROPERTY_GRID_SUBJECT_PROPERTY) {
      sectionIsNone = !otherDisplayName?.trim();
    }
  } else if (feature) {
    const hasFeature = watchedValue;
    if (hasArvOption && propertyId === PROPERTY_GRID_SUBJECT_PROPERTY) {
      const arvHasFeature = arvWatchedValue;
      if (!arvHasFeature) arvSectionIsNone = true;
    }

    if (!hasFeature) sectionIsNone = true;
  }

  return (
    <>
      <TitleRow
        columnIndex={columnIndex}
        fieldTypes={fieldTypes}
        title={title}
        feature={feature}
        isRemovable={isRemovable}
        onRemove={onRemove}
        control={control}
        getValues={getValues}
      />
      {fields.map((field) => (
        <PropertyGridRow
          key={field.manualAdjustmentKey ?? field.fieldName}
          field={field}
          sectionIsNone={sectionIsNone}
          arvSectionIsNone={arvSectionIsNone}
          {...propertySectionProps}
        />
      ))}
    </>
  );
};
